import type { Doc } from "building-types/doc";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { docHasAssociationTableMembership } from "../docAssociationTableTools";
import { docEntityTypeToHashJoinTableName } from "../docHashJoinTableTools";
import { isReverseDocRefEligible, shouldTraverseReverseReferencesForTargetType } from "./docEntityConstraintTools";
import type { DocEntityNode } from "./docEntityNode";
import type { DocEntityNodeSource } from "./docEntityNodeRegistryTypes";

/**
 * Appends the reverse-reference children that doc-ref dependency expansion owns.
 *
 * Reverse traversal is intentionally scoped:
 * 1) Hash-join backed "membership" entities (e.g. componentMembers, entityGroups, etc)
 *    always include reverse references because membership lives outside parent-child edges.
 * 2) Association-table members (e.g. option sets instantiated on a component), which are members of
 *    their container without being one of its dependencies.
 * 3) Non-hash-join entities only include reverse references when explicitly allowed by
 *    docEntityConstraints (generated reverse-traversal target map + FK-level eligibility check).
 *
 * This keeps doc-ref dependency expansion deterministic and prevents broad reverse walks.
 *
 * Every expansion path shares this one implementation so their rules cannot drift apart. They did
 * once: the tree builder covered association members while the traversal helpers did not, so
 * re-deriving a doc ref's sources dropped them and membership sync pruned them as stale.
 */
export function appendDocRefReverseReferenceChildren(
  doc: Doc,
  node: DocEntityNode,
  nodeSource: DocEntityNodeSource,
  childrenAndRefs: DocEntityNode[],
): void {
  const docJoinHashTableName = docEntityTypeToHashJoinTableName(node.getEntityType());
  const reverseTraversalByConstraint = shouldTraverseReverseReferencesForTargetType(node.getEntityType());

  if (!docJoinHashTableName && !reverseTraversalByConstraint) {
    return;
  }

  const nodeEntityType = node.getEntityType();
  const nodeId = node.getId();
  const docJoinHashTable = docJoinHashTableName ? doc[docJoinHashTableName] : undefined;
  if (docJoinHashTableName) {
    assertIsNotUndefined(docJoinHashTable);
  }

  for (const reverseReference of node.getReverseReferences()) {
    const reverseReferenceNode = nodeSource.getNode(doc, reverseReference);
    if (!reverseReferenceNode) {
      continue;
    }

    const isReverseEligible = isReverseDocRefEligible(reverseReferenceNode.getEntityType(), nodeEntityType);
    const isHashJoinMember =
      !!docJoinHashTable && !!nodeId && docJoinHashTable[reverseReferenceNode.getHandleHash()] === nodeId;
    const isAssociationTableMember =
      !!nodeId && docHasAssociationTableMembership(doc, reverseReferenceNode.getHandle(), nodeEntityType, nodeId);

    if (isHashJoinMember || isAssociationTableMember || isReverseEligible) {
      childrenAndRefs.push(reverseReferenceNode);
    }
  }
}
